import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/incentives/index';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildModelYearsPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { getIncentivesMakeBreadcrumbs } from 'client/site-modules/incentives/utils/breadcrumbs';
import { Breadcrumbs } from 'client/site-modules/shared/components/breadcrumbs/breadcrumbs';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { IncentivesByModel } from 'client/site-modules/incentives/components/incentives-by-model/incentives-by-model';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';

import './incentives.scss';

function IncentivesMakeUI({ makeName = null, models = null }) {
  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  if (!makeName || !models) {
    return null;
  }

  const hasElectricModel = models.some(({ engineTypes }) => engineTypes.includes('Electric'));

  return (
    <Container className="incentives incentives-make" tag="main" id="main-content" tabIndex={-1}>
      <div data-tracking-parent="edm-incentives-search-header">
        <Breadcrumbs className="ps-0 mb-2" data={getIncentivesMakeBreadcrumbs(makeName)} />
        <h1 className="heading-2 mb-0_5">{makeName} Tax Credits, Rebates and Incentives</h1>
        <Row className="mb-1_5">
          <Col xs={12} lg={8}>
            {hasElectricModel ? (
              <Fragment>
                Tax credits and rebates are a great way to save money on your next {makeName} purchase or lease. We’ve
                collected all of the available tax credits, state rebates, local programs and automaker incentives for{' '}
                {makeName} electric, plug-in hybrids and gas-powered vehicles.
              </Fragment>
            ) : (
              <Fragment>
                Rebates are a great way to save money on your next {makeName} purchase or lease. We’ve collected all of
                the available rebates and incentives for {makeName} vehicles.
              </Fragment>
            )}
          </Col>
        </Row>
      </div>
      <Row data-tracking-parent="edm-incentives-vehicle-cards">
        <Col xs={12} lg={8}>
          <IncentivesByModel models={models} makeName={makeName} />
        </Col>
        <Col xs={12} lg={4} className="ad-order">
          <div className="py-3 d-sm-inline-block d-lg-block">
            <MrectNative position={'1'} useMinHeight />
          </div>
          <div className="container standard-spacing-first" />
          <div className="pt-2_5 pb-2 d-sm-inline-block d-lg-block">
            <MrectNative position={'2'} useMinHeight addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE} />
          </div>
        </Col>
      </Row>
      <PageLinksWidget divideSections={false} title="Related information" hasTopDivider />
    </Container>
  );
}

IncentivesMakeUI.propTypes = {
  makeName: PropTypes.string,
  models: VehicleEntities.ModelYearsByMakeId,
};

export const IncentivesMake = connectToModel(IncentivesMakeUI, {
  makeName: bindToPath('makes', VehicleModel, (makes, { makeSlug }) => get(makes, [makeSlug, 'name'])),
  models: bindToPath(({ makeSlug }) => buildModelYearsPath(makeSlug), VehicleModel),
});
